/**
 * @fileoverview The phone's side of skipping exact photo copies (B61). The server decides what a copy is
 * (services/photos/photoCopies: the same bytes, or the same Google Photos item, on the gardener's tray, plants or
 * beds) and answers `duplicate_of` instead of storing it; this only reads that answer, sends the Google Photos item id
 * with a photo that came from there, and re-sends a skipped photo with `keepCopy` when the gardener taps Undo.
 *
 * Plain TypeScript with no React, so node --test can run it (photoCopies.test.mjs); the message itself is
 * photoCopies.tsx.
 *
 * Where: every API call that adds a photo (shared/api/tray, photos, packets).
 * Words (en/es): photoCopies.skippedOne, photoCopies.skipped, photoCopies.undo.
 *
 * @module photoCopiesCore
 */

/** What the add-photo calls take to carry a photo's fingerprint. */
export interface CopyOptions {
  /** Google Photos' id for the item, when the photo was picked there. */
  googleItemId?: string | null;
  /** True on Undo: store the photo even if the server says it is a copy. */
  keepCopy?: boolean;
}

/**
 * A photo the server skipped as an exact copy. Thrown by the add-photo calls so no caller mistakes it for a stored
 * photo; the callers count it, and say it once with {@link undoAll} behind Undo. Not a failure: nothing is reported.
 */
export class PhotoCopySkipped<T = unknown> extends Error {
  /** Adds the photo after all, as its own photo (Undo); resolves with the stored photo. */
  readonly resend: () => Promise<T>;
  /** Where the photo it copies is, as the server said (duplicate_of); null when the answer did not say. */
  readonly duplicateOf: CopyPlace | null;

  /**
   * @param resend - The same upload again with keepCopy set.
   * @param duplicateOf - The server's duplicate_of, read by readCopyPlace (the tray's retry, B82, finds the photo by it).
   */
  constructor(resend: () => Promise<T>, duplicateOf: CopyPlace | null = null) {
    super('This photo is already added.');
    this.name = 'PhotoCopySkipped';
    this.resend = resend;
    this.duplicateOf = duplicateOf;
  }
}

/** Where an exact copy's original is: on the tray, on a plant or on a bed, and its id there. */
export interface CopyPlace {
  /** 'tray', 'photo' or 'bed', as the server says. */
  source: string;
  /** Its id: a tray_id for 'tray'. */
  id: number;
}

/**
 * The place the server's duplicate_of names, when the answer carries a usable one.
 *
 * @param data - The response body.
 * @returns The source and id, or null.
 */
export function readCopyPlace(data: unknown): CopyPlace | null {
  const place = typeof data === 'object' && data !== null ? (data as { duplicate_of?: unknown }).duplicate_of : null;
  if (typeof place !== 'object' || place === null) return null;
  const { source, id } = place as { source?: unknown; id?: unknown };
  return typeof source === 'string' && typeof id === 'number' && Number.isFinite(id) ? { source, id } : null;
}

/**
 * Whether the server's answer says the photo was an exact copy and was skipped.
 *
 * @param data - The response body.
 * @returns True when it carries duplicate_of.
 */
export function isCopyAnswer(data: unknown): boolean {
  return typeof data === 'object' && data !== null && 'duplicate_of' in data && Boolean((data as { duplicate_of?: unknown }).duplicate_of);
}

/**
 * The form fields that carry a photo's fingerprint to the server.
 *
 * @param options - The Google Photos item id, and keepCopy on Undo.
 * @returns Fields to append to the multipart form (none when there is nothing to send).
 */
export function copyFields(options?: CopyOptions): Record<string, string> {
  const fields: Record<string, string> = {};
  const id = options?.googleItemId?.trim();
  if (id) fields.googleItemId = id;
  if (options?.keepCopy) fields.keepCopy = '1';
  return fields;
}

/**
 * Which words say it: one copy or several.
 *
 * @param count - How many copies were skipped.
 * @returns The locale key.
 */
export function skippedKey(count: number): 'photoCopies.skippedOne' | 'photoCopies.skipped' {
  return count === 1 ? 'photoCopies.skippedOne' : 'photoCopies.skipped';
}

/**
 * Undo: adds every skipped photo after all. One that fails to go does not stop the others; each failure is handed to
 * onFailure, which reports it and tells the gardener (the customer error standard, photoCopies.tsx).
 *
 * @param skipped - The copies that were skipped.
 * @param onFailure - Told of each copy that could not be added, with what was thrown.
 * @returns What was stored, in order, for the ones that went.
 */
export async function undoAll<T>(skipped: PhotoCopySkipped<T>[], onFailure: (error: unknown) => void): Promise<T[]> {
  const stored: T[] = [];
  for (const copy of skipped) {
    try {
      stored.push(await copy.resend());
    } catch (error) {
      onFailure(error);
    }
  }
  return stored;
}

/** The end of the line of photo sends: each waits for the one before it. */
let sends: Promise<unknown> = Promise.resolve();

/**
 * Runs a photo send after the ones before it have answered, so two copies sent at once cannot both pass the
 * server's check for an exact copy. A failure of an earlier send does not stop the next.
 *
 * @param send - The request.
 * @returns What the request returned, or what it threw.
 */
export function oneAtATime<T>(send: () => Promise<T>): Promise<T> {
  const mine = sends.catch(() => undefined).then(send);
  sends = mine;
  return mine;
}
